import {
    CREATE_SMS_STEP,
    SEND_SMS_MANUAL_GROUP_KEY,
    SEND_SMS_SCHEDULE_TYPE,
} from '@/constants/sms';
import {
    getAllBrandNames,
    getTryDemoBrandnameCondition,
    getTryDemoBrandnameContent,
} from '@/services/sms.service';
import { cloneDeep } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

const SMS_CREATING_INIT_DATA = {
    currentStep: CREATE_SMS_STEP.STEP_1,
    skipToStep: CREATE_SMS_STEP.STEP_1,
    smsData: {
        subject: '',
        content: '',
        messageType: null,
        brandName: null,
    },
    /**
     * @typedef {Object} Customer
     * @property {String} phoneNumber Customer's phone number
     * @property {String} name Customer's name
     * @property {String} birthday Customer's birthday
     */
    /**
     * @type {Array<{name: String, id: String, customers: Array<Customer>}>}
     * @example
     * [
     *     {
     *         name: '',
     *         id: '',
     *         customers: [{phoneNumber: '', name: '', birthday: ''}],
     *     }
     * ],
     */
    customerGroup: [],
    /**
     * @type {Array<{id: String, phoneNumber: String, name: String, birthday: String, groupId: String}>}
     * @example
     * [
     *     {
     *       id: '', phoneNumber: '', name: '', birthday: '', groupId: '',
     *     },
     *  ]
     */
    receivers: [],
    scheduleData: {
        scheduleType: SEND_SMS_SCHEDULE_TYPE.NOW.value,
        birthdaySendBefore: {
            days: 0,
            time: '',
        },
        date: new Date(),
        timezone: undefined,
    },
};

export const SmsModule = {
    state: () => ({
        smsCreating: cloneDeep(SMS_CREATING_INIT_DATA),
        brandNamesFetched: false,
        brandNames: [],
        trialDemoBrandname: {
            conditionFetched: false,
            contentFetched: false,
            condition: false,
            content: '',
        },
    }),
    mutations: {
        setStep(state, data) {
            state.smsCreating.skipToStep = data;
        },
        setSMSData(state, data) {
            state.smsCreating.smsData = data;
        },
        addCustomerGroup(state, data) {
            const name = this._vm.$helpers.autoGenDuplicatedName(
                state.smsCreating.customerGroup.map((item) => item.name),
                data.name
            );
            state.smsCreating.customerGroup.push({ ...data, name });
        },
        addReceiver(state, data) {
            const currentReceivers = state.smsCreating.receivers;
            state.smsCreating.receivers = currentReceivers.concat(data);
        },
        setReceivers(state, data) {
            state.smsCreating.receivers = data;
        },
        setReceiver(state, data) {
            const index = state.smsCreating.receivers.findIndex(
                (i) => i.id === data.id
            );
            state.smsCreating.receivers[index] = data;
        },
        setScheduleData(state, data) {
            state.smsCreating.scheduleData = data;
        },
        setBrandNames(state, data) {
            state.brandNames = data.brandNames;
            state.brandNamesFetched = data.brandNamesFetched;
        },
        resetBrandNames(state) {
            state.brandNames = [];
            state.brandNamesFetched = false;
        },
        resetSmsCreatingData(state) {
            state.smsCreating = cloneDeep(SMS_CREATING_INIT_DATA);
        },
        setCurrentStep(state, data) {
            state.smsCreating.currentStep = data;
        },
        setTrialDemoBrandnameConditionFetched(state, data) {
            state.trialDemoBrandname.conditionFetched = data;
        },
        setTrialDemoBrandnameContentFetched(state, data) {
            state.trialDemoBrandname.contentFetched = data;
        },
        setTrialDemoBrandnameCondition(state, data) {
            state.trialDemoBrandname.condition = data;
        },
        setTrialDemoBrandnameContent(state, data) {
            state.trialDemoBrandname.content = data;
        },
        resetTrialDemoBrandname(state) {
            state.trialDemoBrandname.conditionFetched = false;
            state.trialDemoBrandname.contentFetched = false;
            state.trialDemoBrandname.condition = false;
            state.trialDemoBrandname.content = '';
        },
    },
    actions: {
        setStep({ commit }, data) {
            commit('setStep', data);
        },
        setSMSData({ commit }, { subject, content, messageType, brandName }) {
            commit('setSMSData', { subject, content, messageType, brandName });
        },
        addCustomerGroup({ commit }, { name, customers }) {
            const groupId = uuidv4();
            const preparedCustomers = customers.map((e) => {
                return {
                    ...e,
                    groupId,
                    id: uuidv4(),
                };
            });
            commit('addCustomerGroup', {
                name,
                customers: preparedCustomers,
                id: groupId,
            });
        },
        addReceiverFromGroup({ commit, state }, groupId) {
            const group = state.smsCreating.customerGroup.find(
                (e) => e.id === groupId
            );
            if (group && group.customers && group.customers.length > 0) {
                commit('addReceiver', group.customers);
            }
        },
        addReceiver({ commit }, { phoneNumber, name, birthday }) {
            const receiver = {
                phoneNumber,
                name,
                birthday,
                groupId: SEND_SMS_MANUAL_GROUP_KEY,
                id: uuidv4(),
            };
            commit('addReceiver', [receiver]);
        },
        removeReceiverByGroup({ state, commit }, groupId) {
            const result = state.smsCreating.receivers.filter(
                (e) => e.groupId !== groupId
            );

            commit('setReceivers', result);
        },
        removeReceiver({ state, commit }, receiverId) {
            const result = state.smsCreating.receivers.filter(
                (e) => e.id !== receiverId
            );

            commit('setReceivers', result);
        },
        setReceivers({ commit }, receivers) {
            const mappedReceivers = receivers.map((receiver) => {
                return {
                    ...receiver,
                    ...(!receiver.id && { id: uuidv4() }),
                };
            });
            commit('setReceivers', mappedReceivers);
        },
        setReceiver({ commit }, receiver) {
            commit('setReceiver', receiver);
        },
        setScheduleData(
            { commit },
            { scheduleType, birthdaySendBefore, date, timezone }
        ) {
            commit('setScheduleData', {
                scheduleType,
                birthdaySendBefore,
                date,
                timezone,
            });
        },
        async fetchBrandNames({ commit, state }, force = false) {
            if (!state.brandNamesFetched || force) {
                try {
                    const {
                        data: { brandNames },
                    } = await getAllBrandNames();
                    commit('setBrandNames', {
                        brandNames,
                        brandNamesFetched: true,
                    });
                } catch {
                    commit('setBrandNames', {
                        brandNames: [],
                        brandNamesFetched: false,
                    });
                }
            }
        },
        resetSmsCreatingData({ commit }) {
            commit('resetSmsCreatingData');
        },
        setCurrentStep({ commit }, data) {
            commit('setCurrentStep', data);
        },
        async fetchTrialDemoBrandnameCondition({ state, commit }) {
            if (!state.trialDemoBrandname.conditionFetched) {
                try {
                    const {
                        data: { usable },
                    } = await getTryDemoBrandnameCondition();
                    commit('setTrialDemoBrandnameConditionFetched', true);
                    commit('setTrialDemoBrandnameCondition', usable);
                } catch {
                    commit('setTrialDemoBrandnameConditionFetched', false);
                    commit('setTrialDemoBrandnameCondition', false);
                }
            }
        },
        async fetchTrialDemoBrandnameContent({ state, commit }) {
            if (!state.trialDemoBrandname.contentFetched) {
                try {
                    const { data } = await getTryDemoBrandnameContent();
                    commit('setTrialDemoBrandnameContentFetched', true);
                    commit('setTrialDemoBrandnameContent', data);
                } catch {
                    commit('setTrialDemoBrandnameContentFetched', false);
                    commit('setTrialDemoBrandnameContent', '');
                }
            }
        },
    },
    getters: {
        skipToStep(state) {
            return state.smsCreating?.skipToStep || CREATE_SMS_STEP.STEP_1;
        },
        smsData(state) {
            return state.smsCreating.smsData;
        },
        customerGroup(state) {
            return state.smsCreating.customerGroup;
        },
        receivers(state) {
            return state.smsCreating.receivers;
        },
        scheduleData(state) {
            return state.smsCreating.scheduleData;
        },
        brandNameOptions(state) {
            return state.brandNames.map((e) => {
                return {
                    value: e,
                    text: e.name,
                };
            });
        },
        currentStep(state) {
            return state.smsCreating.currentStep;
        },
    },
    namespaced: true,
};
